import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Typography, CardMedia, Icon } from '@mui/material'

import defaultImage from 'assets/images/default_avatar.png'

import AttendanceInfoDrawer from '../components/AttendanceInfoDrawer'
import AdxTableV2 from '../../../components/custom/AdxTableV2'
import AttendanceLabelRenderer from 'components/custom/AdxLabelRenderer'
import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'
import { formatTimeToAmPm } from 'services/helper/DateTime'
import AdxCircularLoad from 'components/custom/AdxCircularLoad'

const fallbackFromNumericStatus = (status) => {
  switch (status) {
    case 1:
      return 'ongoing'
    case 2:
      return 'present'
    case 3:
      return 'incomplete'
    case 4:
      return 'absent'
    case 5:
      return 'late'
    case 6:
      return 'late_and_incomplete'
    case 8:
      return 'on_leave'
    default:
      return 'unknown'
  }
}

const isToday = (date) => date && new Date(date).toDateString() === new Date().toDateString()

const getAttendanceStatus = (attendanceDay) => {
  const status =
    attendanceDay?.attendance?.derived_status ||
    attendanceDay?.derived_status ||
    fallbackFromNumericStatus(attendanceDay?.attendance?.status)

  if (status !== 'unknown') return status
  if (attendanceDay?.is_working_day === false) return 'non_working_day'
  if (isToday(attendanceDay?.attendance?.date || attendanceDay?.date)) return 'not_clocked_in_yet'
  return status
}

const AttendanceDailyView = ({
  attendanceData,
  rowsPerPage,
  page,
  handleChangePage,
  handleChangeRowsPerPage,
  count,
  search,
}) => {
  const navigate = useNavigate()

  // Passing data to attendance drawer
  const [drawerState, setDrawerState] = useState({ right: false })
  const [attendanceDetails, setAttendanceDetails] = useState({
    profile_image: '',
    date: '',
    staffId: '',
    staffName: '',
    staffTitle: '',
    staffDept: '',
    status: '',
    type: '',
    clockInTime: '',
    clockInLocation: '',
    clockInNote: '',
    clockOutTime: '',
    clockOutLocation: '',
    clockOutNote: '',
    duration: '',
    overtime: '',
    isHoliday: '',
  })

  const handleOpenDrawer = (row) => {
    const status = getAttendanceStatus(row)
    setAttendanceDetails({
      profile_image: row.staff?.profile_image,
      date: row.attendance?.date,
      staffId: row.staff?.id,
      staffName: row.staff?.name,
      staffTitle: row.staff?.position,
      staffDept: row.staff?.department,
      status,
      type: row.attendance?.clockin?.mode_id,
      clockInTime: row.attendance?.clockin?.clockin,
      clockInLocation: row.attendance?.clockin?.street,
      clockInNote: row.attendance?.clockin?.remarks,
      clockOutTime: row.attendance?.clockout?.clockout,
      clockOutLocation: row.attendance?.clockout?.street,
      clockOutNote: row.attendance?.clockout?.remarks,
      duration: row.attendance?.total_hours,
      overtime: row.attendance?.overtime,
      isHoliday: row.is_holiday,
    })
    setDrawerState({ right: true })
  }

  const columns = useMemo(
    () => [
      {
        Header: 'Employee',
        accessor: 'name',
        Cell: (props) => {
          return (
            <Box
              sx={{
                width: '350px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
                gap: 2,
                p: 2,
              }}
            >
              <CardMedia
                component="img"
                sx={{
                  height: 40,
                  width: 40,
                  objectFit: 'cover',
                  borderRadius: '50%',
                }}
                src={props?.row?.original?.staff?.profile_image || defaultImage}
              />
              <Box
                sx={{
                  wordWrap: 'break-word',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'start',
                  alignItems: 'start',
                  maxHeight: 80,
                }}
              >
                <Typography
                  sx={{
                    maxHeight: 40,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordBreak: 'break-word',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                  onClick={() => navigate(`individual/${props?.row?.original?.staff?.id}`)}
                >
                  {props?.row?.original?.staff?.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'light-gray-text.main' }}>
                  {props?.row?.original?.staff?.position}, {props?.row?.original?.staff?.department}
                </Typography>
              </Box>
            </Box>
          )
        },
      },
      {
        Header: 'Clock In',
        accessor: 'clock_in',
        Cell: (props) => {
          return (
            <Box sx={{ p: 2 }}>
              <Typography sx={{}}>
                {props?.row?.original?.attendance?.clockin?.clockin
                  ? formatTimeToAmPm(props?.row?.original?.attendance?.clockin?.clockin)
                  : '--:--'}
              </Typography>
            </Box>
          )
        },
      },
      {
        Header: 'Clock Out',
        accessor: 'clock_out',
        Cell: (props) => {
          return (
            <Box sx={{ p: 2 }}>
              <Typography sx={{}}>
                {props?.row?.original?.attendance?.clockout?.clockout
                  ? formatTimeToAmPm(props?.row?.original?.attendance?.clockout?.clockout)
                  : '--:--'}
              </Typography>
            </Box>
          )
        },
      },
      {
        Header: 'Duration',
        accessor: 'duration',
        Cell: (props) => {
          return (
            <Box sx={{ p: 2 }}>
              <Typography sx={{}}>
                {props?.row?.original?.attendance?.total_hours
                  ? `${props?.row?.original?.attendance?.total_hours} hrs`
                  : '--:--'}
              </Typography>
            </Box>
          )
        },
      },
      {
        Header: 'Overtime',
        accessor: 'overtime',
        Cell: (props) => {
          return (
            <Box sx={{ p: 2 }}>
              <Typography sx={{}}>
                {props?.row?.original?.attendance?.overtime
                  ? `+ ${props?.row?.original?.attendance?.overtime}`
                  : '--:--'}
              </Typography>
            </Box>
          )
        },
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: (props) => {
          const status = getAttendanceStatus(props?.row?.original)
          return (
            <Box sx={{ p: 2, width: 'fit-content', maxWidth: '200px' }}>
              <AttendanceLabelRenderer
                status={status}
                withText
                withChip
                forDaily
                isHoliday={props?.row?.original?.is_holiday}
              />
            </Box>
          )
        },
      },
    ],
    [attendanceData]
  )

  return (
    <>
      <Box>
        {attendanceData?.attendances?.length > 0 ? (
          <AdxTableV2
            column={columns}
            data={attendanceData.attendances}
            allColumnSorted={false}
            sxNav
            sxCell
            count={count}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            cellBorder={false}
            fullWidth={true}
            onClick={handleOpenDrawer}
            rowClickable
            stickyHeader={true}
            stickyFirstColumn={true}
            height={{ xs: 'calc(100vh - 395px)', lg: 'calc(100vh - 326px)' }}
            pagination
            highlightWhenHovered
          />
        ) : search.length > 0 ? (
          <AdxEmptyStateV2
            icon={<Icon>search_off</Icon>}
            title="No results found"
            subtitle={`It looks like we couldn't find any matches for your search. Try using different keywords.`}
          />
        ) : (
          <AdxCircularLoad />
        )}
      </Box>
      <AttendanceInfoDrawer
        drawerState={drawerState}
        setDrawerState={setDrawerState}
        attendanceDetails={attendanceDetails}
      />
    </>
  )
}

export default AttendanceDailyView
